import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "sonner";
import { Loader2, FileText, CheckCircle2, AlertCircle, Download, DownloadCloud, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Session 95 — phase code → human label for the in-flight progress
// readout. Backend writes the machine codes to `phase` on the queue
// doc; we map them here so authors can see "Filling Word template…"
// instead of "render_docx". Unknown phases fall back to the raw code
// so we never hide diagnostic info.
const PHASE_LABELS = {
  init:               "Queued",
  template_lookup:    "Looking up template",
  render:             "Rendering",
  render_docx_loops:  "Expanding table loops",
  render_docx:        "Filling Word template",
  render_docx_post:   "Applying center-align",
  render_xlsx_loops:  "Expanding spreadsheet loops",
  render_xlsx:        "Filling Excel template",
  render_pdf:         "Filling PDF template",
  render_fallback:    "Rendering placeholder doc",
  upload:             "Uploading to cloud storage",
  done:               "Done",
};

function phaseLabel(code) {
  if (!code) return null;
  return PHASE_LABELS[code] || code;
}

// "24s" — seconds elapsed since `iso` (a backend timestamp string).
// Returns null when iso isn't a string or doesn't parse.
function elapsedLabel(iso) {
  if (!iso || typeof iso !== "string") return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  return `${secs}s`;
}

/**
 * DocumentGenerationNotice
 *
 * Fixed bottom-right toast that tracks background document rendering started
 * by create_document Logic Engine actions. Polls /api/logic/documents/status
 * every 2 seconds until every document resolves to `uploaded`,
 * `generated_not_uploaded`, or `failed`.
 *
 * Props:
 *   pendingDocs    : [{ document_id, name }]            — queued docs to track
 *   onDismiss      : () => void                         — clear pending list
 *   headers        : axios auth headers
 *
 * Each row in the toast is independently clickable: once a document's status
 * becomes `uploaded`, clicking the row streams the file via the authenticated
 * `/api/logic/documents/{id}/download` endpoint and triggers a local save.
 * All user types (Lawyer / Admin / Client / DIY) can download; the backend
 * enforces tenant + ownership checks.
 */
export default function DocumentGenerationNotice({ pendingDocs, onDismiss, headers }) {
  const [docs, setDocs]           = useState(pendingDocs || []);
  const [allDone, setAllDone]     = useState(false);
  const [zipping, setZipping]     = useState(false);
  const [zipError, setZipError]   = useState(null);
  const pollRef                   = useRef(null);
  // Tick every second so the per-row "Filling Word template… 24s"
  // counter advances smoothly between the 2-second status polls.
  const [, _tickElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => _tickElapsed((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Reset whenever caller pushes a new batch
  useEffect(() => {
    setDocs(pendingDocs || []);
    setAllDone(false);
  }, [pendingDocs]);

  const ids = (docs || []).map(d => d.document_id).filter(Boolean);

  const poll = useCallback(async () => {
    if (!ids.length) return;
    try {
      const r = await axios.get(
        `${API}/api/logic/documents/status?ids=${encodeURIComponent(ids.join(","))}`,
        { headers }
      );
      const latest = r.data?.documents || [];
      const byId   = Object.fromEntries(latest.map(d => [d.document_id, d]));
      setDocs(prev => prev.map(p => byId[p.document_id]
        ? { ...p, ...byId[p.document_id] }
        : p));
      const done = latest.every(d =>
        ["uploaded", "generated_not_uploaded", "failed"].includes(d.status)
      );
      if (done && latest.length === ids.length) {
        setAllDone(true);
      }
    } catch (e) {
      // Silent failure — keep polling until manually dismissed
    }
  }, [ids.join(","), headers]);

  useEffect(() => {
    if (!ids.length) return;
    // Kick off immediately, then poll every 2 seconds until done
    poll();
    pollRef.current = setInterval(() => {
      poll();
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [ids.join(",")]);

  useEffect(() => {
    if (allDone) clearInterval(pollRef.current);
  }, [allDone]);

  if (!docs || docs.length === 0) return null;

  const total     = docs.length;
  const completed = docs.filter(d => d.status === "uploaded").length;
  const failed    = docs.filter(d => d.status === "failed" || d.status === "generated_not_uploaded").length;
  // Progress is "every doc that reached a terminal state" out of total.
  // Failed counts toward progress so the bar fills smoothly even when
  // some docs fail — it represents work DONE, not work SUCCEEDED.
  const settled       = completed + failed;
  const percent       = total > 0 ? Math.round((settled / total) * 100) : 0;
  const barColor      = failed > 0 && allDone
    ? (completed === 0 ? "bg-red-500" : "bg-amber-500")
    : "bg-emerald-500";

  // Fetch a single document's bytes + filename via the authenticated endpoint.
  const fetchDocBlob = async (d) => {
    const r = await fetch(`${API}/api/logic/documents/${d.document_id}/download`, {
      headers,
      redirect: "follow",
    });
    if (!r.ok) throw new Error(`download failed: ${r.status}`);
    const blob = await r.blob();
    return { name: d.file_name || d.name || `document-${d.document_id}`, blob };
  };

  // Bundle every `uploaded` row into a single .zip on the client and save it.
  // All fetches reuse the same authenticated pattern as per-row clicks, so the
  // Bearer token never appears in a URL and the cloud-provider signed links
  // stay server-side.
  const handleDownloadAll = async () => {
    if (zipping) return;
    const uploaded = docs.filter((d) => d.status === "uploaded");
    if (uploaded.length === 0) return;
    setZipping(true);
    setZipError(null);
    try {
      const zip = new JSZip();
      // Fetch in parallel — these hit the same-origin API; browsers cap at ~6.
      const fetched = await Promise.all(uploaded.map(fetchDocBlob));
      // Deduplicate filenames inside the zip (just in case two docs share a name).
      const seen = Object.create(null);
      for (const { name, blob } of fetched) {
        let finalName = name;
        if (seen[finalName]) {
          const dot = finalName.lastIndexOf(".");
          const stem = dot > 0 ? finalName.slice(0, dot) : finalName;
          const ext  = dot > 0 ? finalName.slice(dot)   : "";
          finalName = `${stem} (${seen[name] + 1})${ext}`;
        }
        seen[name] = (seen[name] || 0) + 1;
        zip.file(finalName, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const stamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const href  = URL.createObjectURL(zipBlob);
      const a     = document.createElement("a");
      a.href      = href;
      a.download  = `filings-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (err) {
      setZipError(err?.message || "Download failed");
    } finally {
      setZipping(false);
    }
  };

  // Portaled to <body> at a z-index above dialogs/overlays (Radix dialogs
  // sit at z-50) so the card is always bottom-right and always visible —
  // even with the Draft Email popup open on top of the page.
  //
  // `pointer-events-auto` is LOAD-BEARING, not cosmetic. An open modal Radix
  // layer (the full-screen Logic Engine editor, the Draft Email modal, any
  // Dialog) sets `pointer-events: none` on <body>, and this portal is a CHILD
  // of <body> — so the card stayed visible above the overlay while every
  // button inside it went dead (hit-testing fell straight through to the
  // dialog's backdrop). The card has to opt itself back in.
  //
  // The handlers stop those events reaching `document`, where the host layer
  // listens. Without them the card is clickable but clicking it counts as an
  // "outside interaction": Radix's pointerdown-outside would CLOSE the dialog
  // underneath (losing an editor full of work), and its focus trap would yank
  // focus off the button on focusin. Bubble phase only — capture would kill
  // the clicks we just restored.
  return createPortal(
    <div
      className="fixed bottom-5 right-5 z-[2000] w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-xl border border-gray-200 shadow-2xl overflow-hidden pointer-events-auto"
      data-testid="doc-generation-notice"
      onPointerDown={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
        {allDone ? (
          failed === total ? (
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
          ) : (
            <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
          )
        ) : (
          <Loader2 size={16} className="text-emerald-600 animate-spin flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-800 truncate">
            {allDone
              ? (failed === total ? "Document generation failed" : "Documents ready")
              : "Generating documents…"}
          </div>
          <div className="text-xs text-gray-500">
            {allDone
              ? `${completed} of ${total} uploaded${failed > 0 ? ` · ${failed} failed` : ""}`
              : `${completed} of ${total} complete`}
          </div>
        </div>
        {/* Download-all: show only when 2+ docs are uploaded; zip them client-side. */}
        {completed >= 2 && (
          <button
            onClick={handleDownloadAll}
            disabled={zipping}
            title={zipError ? `Retry (${zipError})` : `Download all ${completed} files as .zip`}
            aria-label="Download all documents as zip"
            className={[
              "flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[14px] font-medium transition-colors",
              zipping
                ? "bg-emerald-100 text-emerald-600 cursor-wait"
                : zipError
                  ? "bg-red-50 text-red-600 hover:bg-red-100"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
            ].join(" ")}
            data-testid="doc-notice-download-all-btn"
          >
            {zipping
              ? <Loader2 size={14} className="animate-spin" />
              : <DownloadCloud size={14} />}
            {zipping ? "Zipping…" : "All"}
          </button>
        )}
        <button
          onClick={onDismiss}
          className="text-gray-300 hover:text-gray-600 transition-colors"
          data-testid="doc-notice-close-btn"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Progress bar — Session 95. Fills smoothly as each doc reaches a
          terminal state. Color shifts to amber on partial failure and
          red on total failure so the user reads outcome at a glance. */}
      <div
        className="h-1 bg-gray-100 overflow-hidden"
        data-testid="doc-notice-progress-bar"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${settled} of ${total} documents settled`}
      >
        <div
          className={`h-full ${barColor} transition-[width] duration-500 ease-out`}
          style={{ width: `${percent}%` }}
          data-testid="doc-notice-progress-bar-fill"
          data-percent={percent}
        />
      </div>

      {/* Document list */}
      <ul className="max-h-72 overflow-y-auto divide-y divide-gray-50" data-testid="doc-notice-list">
        {docs.map((d) => {
          const isDone   = d.status === "uploaded";
          const isFailed = d.status === "failed" || d.status === "generated_not_uploaded";

          const handleDownload = () => {
            if (!isDone) return;
            // Secure download: send the Bearer header via fetch, stream the
            // redirected cloud-provider URL into a Blob, and trigger a local
            // save. The JWT never leaves the Authorization header.
            fetch(`${API}/api/logic/documents/${d.document_id}/download`, {
              headers,
              redirect: "follow",
            })
              .then((r) => {
                if (!r.ok) throw new Error(`download failed: ${r.status}`);
                return r.blob();
              })
              .then((blob) => {
                const href = URL.createObjectURL(blob);
                const a    = document.createElement("a");
                a.href     = href;
                a.download = d.file_name || d.name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(href);
              })
              // A new tab can't carry the Bearer token (it always showed
              // "Not authenticated"), so report the failure in place.
              .catch((err) => {
                toast.error(`Couldn't download ${d.file_name || d.name || "the document"}`, {
                  description: err?.message || "Please try again.",
                });
              });
          };

          return (
            <li
              key={d.document_id}
              role={isDone ? "button" : undefined}
              tabIndex={isDone ? 0 : -1}
              onClick={handleDownload}
              onKeyDown={(e) => {
                if (!isDone) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleDownload();
                }
              }}
              title={isDone ? `Download ${d.file_name || d.name}` : undefined}
              className={[
                "group flex items-center gap-2 px-4 py-2.5 transition-colors",
                isDone ? "cursor-pointer hover:bg-emerald-50/60 focus:bg-emerald-50/60 focus:outline-none" : "",
              ].join(" ")}
              data-testid={`doc-notice-item-${d.document_id}`}
            >
              {isDone ? (
                <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
              ) : isFailed ? (
                <AlertCircle size={13} className="text-red-400 flex-shrink-0" />
              ) : (
                <Loader2 size={13} className="text-gray-400 animate-spin flex-shrink-0" />
              )}
              <FileText size={12} className="text-gray-300 flex-shrink-0 group-hover:text-gray-400" />
              <div className="flex-1 min-w-0">
                <div
                  className={[
                    "text-xs truncate",
                    isDone ? "text-gray-700 group-hover:text-emerald-700 group-hover:underline decoration-dotted underline-offset-2" : "text-gray-700",
                  ].join(" ")}
                  title={d.file_name || d.name}
                >
                  {d.file_name || d.name}
                </div>
                {/* Session 95 — phase label + elapsed seconds while
                    rendering. Backend writes a fresh `phase` and
                    `phase_started_at` to the queue doc on every
                    transition (template_lookup → render_docx_loops →
                    render_docx → upload → done). For finished rows
                    we surface the heaviest phase from `phase_timings`
                    so users can spot slow templates. */}
                {!isDone && !isFailed && d.phase && d.phase !== "done" && (
                  <div
                    data-testid={`doc-notice-phase-${d.document_id}`}
                    className="text-[10px] text-gray-400 truncate"
                  >
                    {phaseLabel(d.phase)}
                    {elapsedLabel(d.phase_started_at)
                      ? ` · ${elapsedLabel(d.phase_started_at)}`
                      : ""}
                  </div>
                )}
                {isFailed && d.error_phase && (
                  <div
                    data-testid={`doc-notice-error-phase-${d.document_id}`}
                    className="text-[10px] text-red-500 truncate"
                  >
                    failed in {phaseLabel(d.error_phase)}
                  </div>
                )}
              </div>
              {isDone && (
                <Download
                  size={12}
                  className="text-gray-300 group-hover:text-emerald-600 flex-shrink-0 transition-colors"
                  aria-hidden="true"
                  data-testid={`doc-notice-download-${d.document_id}`}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  );
}
